import * as d3 from 'd3';

export default class ForceGraph {
  elementId = '';
  el = {};

  constructor(params?: { elementId: string }) {
    this.elementId = params.elementId;
  }

  build(
    {
      nodes, // an iterable of node objects (typically [{id}, …])
      links, // an iterable of link objects (typically [{source, target}, …])
    },
    {
      nodeId = (d: { id: any }) => d.id, // given d in nodes, returns a unique identifier (string)
      // @ts-ignore
      nodeGroup, // given d in nodes, returns an (ordinal) value for color
      // @ts-ignore
      nodeGroups, // an array of ordinal values representing the node groups
      // @ts-ignore
      nodeTitle, // given d in nodes, a title string
      nodeFill = 'currentColor', // node stroke fill (if not using a group color encoding)
      nodeStroke = '#fff', // node stroke color
      nodeStrokeWidth = 1.5, // node stroke width, in pixels
      nodeStrokeOpacity = 1, // node stroke opacity
      nodeRadius = 5, // node radius, in pixels
      // @ts-ignore
      nodeStrength,
      linkSource = ({ source }) => source, // given d in links, returns a node identifier string
      linkTarget = ({ target }) => target, // given d in links, returns a node identifier string
      linkStroke = '#999', // link stroke color
      linkStrokeOpacity = 0.6, // link stroke opacity
      linkStrokeWidth = 1.5, // given d in links, returns a stroke width in pixels
      linkStrokeLinecap = 'round', // link stroke linecap
      // @ts-ignore
      linkStrength,
      //   colors = d3.schemeTableau10, // an array of color strings, for the node groups
      colors = d3.schemePaired, // an array of color strings, for the node groups

      width = 500, // outer width, in pixels
      height = 500, // outer height, in pixels
      // @ts-ignore
      invalidation, // when this promise resolves, stop the simulation
    } = {}
  ) {
    // Compute values.
    const N = d3.map(nodes, nodeId).map(intern);
    const LS = d3.map(links, linkSource).map(intern);
    const LT = d3.map(links, linkTarget).map(intern);
    if (nodeTitle === undefined) nodeTitle = (_: any, i: string | number) => N[i];
    const T = nodeTitle == null ? null : d3.map(nodes, nodeTitle);
    const G = nodeGroup == null ? null : d3.map(nodes, nodeGroup).map(intern);
    const W = typeof linkStrokeWidth !== 'function' ? null : d3.map(links, linkStrokeWidth);

    // Replace the input nodes and links with mutable objects for the simulation.
    nodes = d3.map(nodes, (_, i) => ({ id: N[i] }));
    links = d3.map(links, (_, i) => ({ source: LS[i], target: LT[i] }));

    // Compute default domains.
    if (G && nodeGroups === undefined) nodeGroups = d3.sort(G);

    // Construct the scales.
    const color = nodeGroup == null ? null : d3.scaleOrdinal(nodeGroups, colors);

    // Construct the forces.
    const forceNode = d3.forceManyBody();
    const forceLink = d3.forceLink(links).id(({ index: i }) => N[i]);
    if (nodeStrength !== undefined) forceNode.strength(nodeStrength);
    if (linkStrength !== undefined) forceLink.strength(linkStrength);

    const simulation = d3.forceSimulation(nodes).force('link', forceLink).force('charge', forceNode).force('x', d3.forceX()).force('y', d3.forceY()).on('tick', ticked);

    const svg = d3
      .create('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [-width / 2, -height / 2, width, height])
      .attr('style', 'max-width: 100%; height: auto; height: intrinsic;');

    const link = svg
      .append('g')
      .attr('stroke', linkStroke)
      .attr('stroke-opacity', linkStrokeOpacity)
      .attr('stroke-width', typeof linkStrokeWidth !== 'function' ? linkStrokeWidth : null)
      .attr('stroke-linecap', linkStrokeLinecap)
      .selectAll('line')
      .data(links)
      .join('line');

    // @ts-ignore
    if (W) link.attr('stroke-width', ({ index: i }) => W[i]);

    const node = svg
      .append('g')
      .attr('fill', nodeFill)
      .attr('stroke', nodeStroke)
      .attr('stroke-opacity', nodeStrokeOpacity)
      .attr('stroke-width', nodeStrokeWidth)
      .selectAll('circle')
      .data(nodes)
      .join('circle')
      .attr('r', nodeRadius)
      .call(drag(simulation));

    if (G) node.attr('fill', ({ index: i }) => color(G[i]));
    // @ts-ignore
    if (T) node.append('title').text(({ index: i }) => T[i]);

    // Handle invalidation.
    if (invalidation != null) invalidation.then(() => simulation.stop());

    function intern(value: { valueOf: () => any }) {
      return value !== null && typeof value === 'object' ? value.valueOf() : value;
    }

    function ticked() {
      link
        // @ts-ignore
        .attr('x1', (d) => d.source.x)
        // @ts-ignore
        .attr('y1', (d) => d.source.y)
        // @ts-ignore
        .attr('x2', (d) => d.target.x)
        // @ts-ignore
        .attr('y2', (d) => d.target.y);
      // @ts-ignore
      node.attr('cx', (d) => d.x).attr('cy', (d) => d.y);
    }

    function drag(simulation: d3.Simulation<d3.SimulationNodeDatum, undefined>) {
      function dragstarted(event: { active: any; subject: { fx: any; x: any; fy: any; y: any } }) {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      }

      function dragged(event: { subject: { fx: any; fy: any }; x: any; y: any }) {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      }

      function dragended(event: { active: any; subject: { fx: any; fy: any } }) {
        if (!event.active) simulation.alphaTarget(0);
        event.subject.fx = null;
        event.subject.fy = null;
      }

      return d3.drag().on('start', dragstarted).on('drag', dragged).on('end', dragended);
    }

    return Object.assign(svg.node(), { scales: { color } });
  }

  loadData(data: { nodes: any[]; links: any[] }, options = {}) {
    const container = d3.select(this.elementId);
    // @ts-ignore
    const graphEl = this.build(data, {
      nodeId: (d) => d.id,
      nodeGroup: (d: { group: any }) => d.group,
      nodeTitle: (d: { id: any; group: any }) => `${d.id} (${d.group})`,
    });

    container.append(() => graphEl);
  }
}
